// Small shopping-flow fixes from the site test (Nov 2026):
// - Mobile filter panel: "Sort" sits at the top, under the "Filter" title, instead of
//   below every filter group.
// - Footer: bigger tap areas for the policy links and social icons on phones (the look
//   stays the same).
// - Search popup: the previous results fade while new ones load, so old results don't
//   look like answers to what was just typed.
// - Cart: a stock message ("Only 2 items were added...") clears when the cart is closed.
// - Cart: with "Auto-open cart drawer" on, the drawer opens straight away on "Add to cart"
//   and shows the item being added at once as a soft blur, until the server's cart replaces it.
// Loaded from snippets/menu-curated-collections.liquid.

const STYLE_ID = 'mujo-ux-fixes-css';

const CSS = `
/* Sort first in the mobile filter panel */
.facets--drawer .facets__form > .facets__title-wrapper { order: -2; }
.facets--drawer .facets__form > sorting-filter-component {
  order: -1;
  padding-block: 0;
  margin-block-end: 0;
  border-block-end: 1px solid color-mix(in srgb, var(--color-foreground) 8%, transparent);
}

/* Footer tap areas on phones */
@media screen and (max-width: 749px) {
  [class*="ai-footer-utilities__links-"] { gap: 0 !important; }
  [class*="ai-footer-utilities__link-"] {
    display: inline-block;
    padding-block: 7px;
  }
  [class*="ai-footer-utilities__social-link-"] {
    display: inline-flex;
    box-sizing: content-box !important;
    padding: 8px !important;
    margin: -8px !important;
  }
}

/* Cart drawer while an item is being added: the item shows straight away (built from the
   product page) as a soft blur, and comes into focus when the cart comes back */
.mujo-pending-row {
  filter: blur(6px);
  opacity: 0.7;
  animation: mujo-pending-pulse 1.4s ease-in-out infinite;
  pointer-events: none;
}
.mujo-pending-row .mujo-pending-note { display: none; }
@keyframes mujo-pending-pulse { 50% { opacity: 0.45; } }
/* The row comes into focus when the real cart line replaces it */
.cart-drawer__dialog .mujo-arrived { animation: mujo-unblur 0.35s ease-out; }
@keyframes mujo-unblur { from { filter: blur(6px); opacity: 0.7; } to { filter: none; opacity: 1; } }
cart-drawer-component[data-mujo-bump] .mujo-bump-row { filter: blur(3px); transition: filter 0.2s ease; }
.mujo-pending-cart {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  background: var(--mujo-cart-tone, var(--color-background));
  pointer-events: none;
}
.cart-drawer__inner:has(> .mujo-pending-cart) { position: relative; }

/* Search popup: fade old results while new ones load */
predictive-search-component [ref="predictiveSearchResults"] { transition: opacity 0.15s ease; }
predictive-search-component[data-mujo-loading] [ref="predictiveSearchResults"] { opacity: 0.35; }
`;

const ensureStyles = () => {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.append(style);
};

// Search: mark as loading on typing, clear once the results change (or after 3s)
const watched = new WeakSet();
const watchSearch = (component) => {
  if (watched.has(component)) return;
  const results = component.querySelector('[ref="predictiveSearchResults"]');
  if (!results) return;
  watched.add(component);
  let timer;
  const done = () => {
    clearTimeout(timer);
    delete component.dataset.mujoLoading;
  };
  new MutationObserver(done).observe(results, { childList: true, subtree: true });
  component.addEventListener('input', (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.name !== 'q') return;
    component.dataset.mujoLoading = '';
    clearTimeout(timer);
    timer = setTimeout(done, 3000);
  });
};

const mountSearch = () => document.querySelectorAll('predictive-search-component').forEach(watchSearch);

// Cart: open the drawer the moment "Add to cart" is pressed (when the theme's "Auto-open
// cart drawer" setting is on) and show the item being added straight away, built from the
// product page, instead of waiting a second or more for the server. The theme's own redraw
// then replaces it with the real cart line.
const ADDING_TIMEOUT = 8000;
let addingTimer;

const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

let pendingShown = false;

const clearPending = () => {
  clearTimeout(addingTimer);
  pendingShown = false;
  document.querySelectorAll('.mujo-pending-row, .mujo-pending-cart').forEach((el) => el.remove());
  document.querySelectorAll('.mujo-bump-row').forEach((el) => el.classList.remove('mujo-bump-row'));
  document.querySelectorAll('cart-drawer-component[data-mujo-bump]').forEach((d) => delete d.dataset.mujoBump);
};

const pendingItem = (form) => {
  const product = window.ShopifyAnalytics?.meta?.product || {};
  const variantId = new FormData(form).get('id');
  const variant = (product.variants || []).find((v) => String(v.id) === String(variantId));
  const section = form.closest('.shopify-section') || document;
  const title = section.querySelector('h1')?.textContent.trim() || variant?.name || '';
  const option = variant?.public_title && variant.public_title !== 'Default Title' ? variant.public_title : '';
  const priceText = section.querySelector('product-price')?.textContent.replace(/\s+/g, ' ').trim() || '';
  const price = (priceText.match(/\$[\d,.]+/) || [''])[0];
  const media = form.closest('product-form-component')?.querySelector('add-to-cart-component')?.dataset.productVariantMedia || '';
  const image = media ? media.replace(/([?&])width=\d+/, '$1width=240') : '';
  const qty = Number(new FormData(form).get('quantity')) || 1;
  return { variantId, title, vendor: product.vendor || '', option, price, image, qty };
};

const rowHtml = (item) => `
  <tr role="row" class="cart-items__table-row mujo-pending-row" aria-hidden="true">
    <td class="cart-items__media" role="cell">
      ${item.image ? `<span class="cart-items__media-container"><img class="cart-items__media-image" src="${esc(item.image)}" alt=""></span>` : ''}
    </td>
    <td class="cart-items__details cart-primary-typography" role="cell">
      ${item.vendor ? `<p class="cart-items__maker">${esc(item.vendor)}</p>` : ''}
      <p><span class="cart-items__title">${esc(item.title)}</span></p>
      ${item.option ? `<dl class="cart-items__variants"><div class="cart-items__variant"><dd>${esc(item.option)}</dd></div></dl>` : ''}
    </td>
    <td class="cart-items__quantity" role="cell"><span class="mujo-pending-note">Adding…</span></td>
    <td class="cart-items__price cart-secondary-typography" role="cell">${esc(item.price)}</td>
  </tr>`;

const showPending = (drawer, form) => {
  clearPending();
  const item = pendingItem(form);
  const dialog = drawer.querySelector('dialog');
  if (!dialog || !item.title) return;

  // Already in the cart: just soften that line until the new quantity arrives
  const existing = item.variantId
    ? dialog.querySelector(`.cart-items__title[href*="variant=${item.variantId}"]`)?.closest('.cart-items__table-row')
    : null;
  if (existing) {
    existing.classList.add('mujo-bump-row');
    drawer.dataset.mujoBump = '';
    return;
  }

  const tbody = dialog.querySelector('.cart-items__table tbody');
  if (tbody) {
    tbody.insertAdjacentHTML('afterbegin', rowHtml(item));
    pendingShown = true;
    return;
  }

  // Empty cart: lay a cart over the empty state until the real one arrives
  const inner = dialog.querySelector('.cart-drawer__inner');
  if (!inner) return;
  inner.insertAdjacentHTML(
    'beforeend',
    `<div class="mujo-pending-cart" aria-hidden="true">
      <div class="cart-drawer__header"><span class="cart-drawer__heading h3">Cart</span></div>
      <div class="cart-drawer__items"><div class="cart-items"><table class="cart-items__table"><tbody>${rowHtml(item)}</tbody></table></div></div>
    </div>`
  );
  pendingShown = true;
};

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('product-form-component')) return;
    const button = form.querySelector('[ref="addToCartButton"]');
    if (!button || button.disabled || button.getAttribute('disabled') === 'true') return;
    const drawer = document.querySelector('cart-drawer-component[auto-open]');
    if (!drawer || typeof drawer.open !== 'function') return;
    try {
      showPending(drawer, form);
    } catch (e) {}
    clearTimeout(addingTimer);
    addingTimer = setTimeout(clearPending, ADDING_TIMEOUT);
    drawer.open();
  },
  true
);
document.addEventListener('cart:update', () =>
  requestAnimationFrame(() => {
    const arrived = pendingShown;
    clearPending();
    if (!arrived) return;
    // Bring the newly added line into focus
    const row = document.querySelector('.cart-drawer__dialog tbody .cart-items__table-row');
    if (!row) return;
    row.classList.add('mujo-arrived');
    setTimeout(() => row.classList.remove('mujo-arrived'), 450);
  })
);
document.addEventListener('cart:error', clearPending);

// Cart: clear stock messages when the cart closes
document.addEventListener(
  'close',
  (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLElement) || !dialog.closest('cart-drawer-component')) return;
    dialog.querySelectorAll('[ref^="cartItemErrorContainer"]').forEach((el) => el.classList.add('hidden'));
  },
  true
);

if (!window.__mujoUxFixes) {
  window.__mujoUxFixes = true;
  ensureStyles();
  mountSearch();
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      mountSearch();
    });
  }).observe(document.body, { childList: true, subtree: true });
}